import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { FileText, AlertTriangle, Users, Download, CheckCircle, XCircle, Clock, Euro, Info, Star, Award, FileSpreadsheet, AlertCircle } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface Client {
    ClientID: string;
    Title: string;
    ClientRef: string;
    ClientState: string;
    ClientType: string;
    UserEmail: string;
    UserLastName: string;
    NatureOfBusiness: string;
    Address1: string;
    Address2: string;
    DateOfIncorporation: string;
    YearEndDate: string;
    Services: string;
    CreatedDate: string;
    UpdatedDate: string;
}

interface EnrichedClient extends Client {
    invoicedFeesYTD: number | null;
    feesOutstanding: number | null;
    healthScore: number | null;
    healthCategory: string | null;
    electedServices: string[];
    contractedValue: number | null;
    contractedValueMethod: '12mo_sum' | 'avg_x12' | 'insufficient' | null;
    invoiceCount: number | null;
    tenureMonths: number | null;
    managerName: string | null;
    managerInferred: boolean;
    isBssClient: boolean;
    isGoldPartner: boolean;
    isPartner: boolean;
}

interface EnrichmentMap {
    [sentaId: string]: {
        invoicedFeesYTD?: number;
        feesOutstanding?: number;
        healthScore?: number;
        healthCategory?: string;
        electedServices?: string[];
        managerName?: string;
        managerInferred?: boolean;
        contractedValue?: number;
        contractedValueMethod?: '12mo_sum' | 'avg_x12' | 'insufficient';
        invoiceCount?: number;
        tenureMonths?: number;
        isBssClient?: boolean;
        isGoldPartner?: boolean;
        isPartner?: boolean;
    };
}

const formatCurrency = (val: number | null): string => {
    if (val === null || val === undefined) return '—';
    return `€${val.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getHealthBadgeClass = (category: string | null): string => {
    switch (category) {
        case 'Healthy': return 'renewable';
        case 'Moderate': return 'moderate';
        case 'At Risk': return 'at-risk';
        case 'Critical': return 'one-off';
        default: return 'offline';
    }
};

const BssReporting: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<EnrichedClient[]>([]);
    const [filteredClients, setFilteredClients] = useState<EnrichedClient[]>([]);
    const [search, setSearch] = useState("");
    const [stateFilter, setStateFilter] = useState<string>("all");
    const [activeOnly, setActiveOnly] = useState(true);
    const [partnerFilter, setPartnerFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);
    const [enrichmentLoaded, setEnrichmentLoaded] = useState(false);


    useEffect(() => {
        const fetchData = async () => {
            if (accounts.length === 0) return;

            setLoading(true);
            try {
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }

                const headers = { 'Authorization': `Bearer ${token}` };
                const baseUrl = getApiBaseUrl();

                // Fetch clients first
                const clientsResponse = await fetch(`${baseUrl}/api/clients`, { headers });
                if (!clientsResponse.ok) throw new Error(`Failed to fetch clients: ${clientsResponse.status}`);
                const clientsData = await clientsResponse.json();

                // Set initial clients (without enrichment)
                const baseClients: EnrichedClient[] = clientsData.clients.map((c: Client) => ({
                    ...c,
                    invoicedFeesYTD: null,
                    feesOutstanding: null,
                    healthScore: null,
                    healthCategory: null,
                    electedServices: [],
                    contractedValue: null,
                    contractedValueMethod: null,
                    invoiceCount: null,
                    tenureMonths: null,
                    managerName: null,
                    managerInferred: false,
                    isBssClient: false,
                    isGoldPartner: false,
                    isPartner: false,
                }));
                setClients(baseClients);

                // Apply enrichment data as soon as it arrives (don't wait for HubSpot).
                // Keep the global progress bar visible until enrichment + the filter
                // useEffect have both finished, so users don't see a half-rendered table.
                const applyEnrichment = async () => {
                    try {
                        const res = await fetch(`${baseUrl}/api/bss-enrichment`, { headers });
                        if (!res.ok) { setLoading(false); return; }
                        const enrichment: EnrichmentMap = await res.json();
                        setClients(prev => prev.map(c => {
                            const e = enrichment[c.ClientID] || {};
                            return {
                                ...c,
                                invoicedFeesYTD: e.invoicedFeesYTD ?? null,
                                feesOutstanding: e.feesOutstanding ?? null,
                                healthScore: e.healthScore ?? null,
                                healthCategory: e.healthCategory ?? null,
                                electedServices: e.electedServices || [],
                                managerName: e.managerName ?? null,
                                managerInferred: e.managerInferred ?? false,
                                contractedValue: e.contractedValue ?? null,
                                contractedValueMethod: e.contractedValueMethod ?? null,
                                invoiceCount: e.invoiceCount ?? null,
                                tenureMonths: e.tenureMonths ?? null,
                                isBssClient: e.isBssClient ?? false,
                                isGoldPartner: e.isGoldPartner ?? false,
                                isPartner: e.isPartner ?? false,
                            };
                        }));
                        setEnrichmentLoaded(true);
                    } catch (err) {
                        console.error('Failed to fetch enrichment:', err);
                        setLoading(false);
                    }
                };

                // Fire enrichment
                applyEnrichment();
            } catch (err: any) {
                setError(err.message || 'Failed to load clients');
                setLoading(false);
            }
        };

        fetchData();
    }, [instance, accounts, setLoading]);

    // Get unique client states with counts
    const clientStates = React.useMemo(() => {
        const stateCounts: { [key: string]: number } = {};
        clients.forEach(c => {
            const state = c.ClientState || 'Unknown';
            stateCounts[state] = (stateCounts[state] || 0) + 1;
        });
        return Object.entries(stateCounts)
            .map(([state, count]) => ({ state, count }))
            .sort((a, b) => b.count - a.count);
    }, [clients]);

    useEffect(() => {
        let filtered = clients;

        if (activeOnly) {
            filtered = filtered.filter(c => c.ClientState === 'Client');
        }

        if (stateFilter !== "all") {
            filtered = filtered.filter(c => (c.ClientState || 'Unknown') === stateFilter);
        }

        if (partnerFilter !== "all" && enrichmentLoaded) {
            if (partnerFilter === "gold") {
                filtered = filtered.filter(c => c.isGoldPartner);
            } else if (partnerFilter === "partner") {
                filtered = filtered.filter(c => c.isPartner && !c.isGoldPartner);
            } else if (partnerFilter === "client") {
                filtered = filtered.filter(c => !c.isGoldPartner && !c.isPartner);
            }
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (c) =>
                    c.Title?.toLowerCase().includes(searchLower) ||
                    c.ClientRef?.toLowerCase().includes(searchLower) ||
                    c.UserEmail?.toLowerCase().includes(searchLower) ||
                    c.NatureOfBusiness?.toLowerCase().includes(searchLower) ||
                    c.Address1?.toLowerCase().includes(searchLower)
            );
        }

        setFilteredClients(filtered);
    }, [search, stateFilter, activeOnly, partnerFilter, clients, enrichmentLoaded]);

    // Hide the global progress bar only after enrichment has applied AND the filter
    // pass above has produced its first post-enrichment result. Defer to the next
    // animation frame so the table actually paints before the bar disappears.
    useEffect(() => {
        if (!enrichmentLoaded) return;
        const raf = requestAnimationFrame(() => setLoading(false));
        return () => cancelAnimationFrame(raf);
    }, [enrichmentLoaded, filteredClients, setLoading]);

    const activeCount = filteredClients.filter(c => c.ClientState === 'Client').length;
    const inactiveCount = filteredClients.filter(c => c.ClientState === 'Former client' || c.ClientState === 'Striking off' || c.ClientState === 'Suspended').length;
    const goldPartnerCount = filteredClients.filter(c => c.isGoldPartner).length;
    const partnerCount = filteredClients.filter(c => c.isPartner && !c.isGoldPartner).length;
    const clientOnlyCount = filteredClients.filter(c => !c.isGoldPartner && !c.isPartner).length;

    // Aggregated financial stats (based on filtered list)
    const totalFeesYTD = filteredClients.reduce((sum, c) => sum + (c.invoicedFeesYTD || 0), 0);
    const totalOutstanding = filteredClients.reduce((sum, c) => sum + (c.feesOutstanding || 0), 0);
    const healthScores = filteredClients.filter(c => c.healthScore !== null && c.healthScore > 0);
    const avgHealthScore = healthScores.length > 0
        ? Math.round(healthScores.reduce((sum, c) => sum + (c.healthScore || 0), 0) / healthScores.length)
        : 0;
    const totalContractedValue = filteredClients.reduce((sum, c) => sum + (c.contractedValue || 0), 0);

    const columns: TableColumn<EnrichedClient>[] = [
        {
            name: "Client Ref",
            selector: (row) => row.ClientRef,
            sortable: true,
            cell: (row) => (
                <code className="sku-code">{row.ClientRef || '—'}</code>
            ),
            width: "100px"
        },
        {
            name: "Client Name",
            selector: (row) => row.Title,
            sortable: true,
            cell: (row) => (
                <div className="service-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="service-name">{row.Title}</span>
                        {row.isGoldPartner && (
                            <Tooltip title="Gold Partner" arrow placement="top">
                                <span style={{ display: 'inline-flex', color: '#f59e0b' }}><Star size={14} fill="#f59e0b" /></span>
                            </Tooltip>
                        )}
                        {row.isPartner && !row.isGoldPartner && (
                            <Tooltip title="Partner" arrow placement="top">
                                <span style={{ display: 'inline-flex', color: '#3b82f6' }}><Award size={14} /></span>
                            </Tooltip>
                        )}
                    </div>
                    {row.NatureOfBusiness && (
                        <div className="service-desc-short">{row.NatureOfBusiness.substring(0, 50)}{row.NatureOfBusiness.length > 50 ? '...' : ''}</div>
                    )}
                </div>
            ),
            grow: 2,
            minWidth: "200px"
        },
        {
            name: "Status",
            selector: (row) => row.ClientState,
            sortable: true,
            cell: (row) => {
                const state = row.ClientState || 'Unknown';
                const isActive = state === 'Active';
                const isInactive = state === 'Inactive' || state === 'Off-boarded';
                return (
                    <span className={`status-badge ${isActive ? 'renewable' : isInactive ? 'one-off' : 'offline'}`}>
                        {isActive ? <><CheckCircle size={12} /> {state}</> :
                         isInactive ? <><XCircle size={12} /> {state}</> :
                         <><Clock size={12} /> {state}</>}
                    </span>
                );
            },
            width: "130px"
        },
        {
            name: "Manager",
            selector: (row) => row.managerName || '',
            sortable: true,
            cell: (row) => (
                row.managerName ? (
                    <span style={row.managerInferred ? { fontStyle: 'italic', color: '#9ca3af' } : undefined}>
                        {row.managerName}
                        {row.managerInferred && (
                            <span
                                title="Inferred from Senta company record (form field _04e5d5385c99) because no staff assignment exists."
                                style={{ marginLeft: 4, fontSize: '0.7em', padding: '1px 4px', background: '#374151', color: '#d1d5db', borderRadius: 3, verticalAlign: 'middle' }}
                            >
                                inferred
                            </span>
                        )}
                    </span>
                ) : <span>—</span>
            ),
            width: "180px"
        },
        {
            name: "Fees YTD",
            selector: (row) => row.invoicedFeesYTD || 0,
            sortable: true,
            cell: (row) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(row.invoicedFeesYTD)}
                </span>
            ),
            right: true,
            width: "120px"
        },
        {
            name: "Outstanding",
            selector: (row) => row.feesOutstanding || 0,
            sortable: true,
            cell: (row) => (
                <span style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: row.feesOutstanding && row.feesOutstanding > 0 ? 'var(--error-color, #ef4444)' : 'inherit'
                }}>
                    {formatCurrency(row.feesOutstanding)}
                </span>
            ),
            right: true,
            width: "120px"
        },
        {
            name: "Health",
            selector: (row) => row.healthScore ?? -1,
            sortable: true,
            cell: (row) => {
                if (row.healthScore === null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                return (
                    <span className={`status-badge ${getHealthBadgeClass(row.healthCategory)}`}>
                        {row.healthScore}
                    </span>
                );
            },
            width: "90px",
            center: true
        },
        {
            name: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Contracted
                    <Tooltip
                        title={
                            <div style={{ whiteSpace: 'pre-line', fontSize: 12, lineHeight: 1.5 }}>
                                Estimated from invoices:{"\n"}
                                • 12+ months tenure: sum of last 12 months{"\n"}
                                • {"<"} 12 months: avg of invoices (excl. first) × 12{"\n"}
                                • ~ prefix = estimated (avg × 12)
                            </div>
                        }
                        arrow
                        placement="top"
                    >
                        <span style={{ cursor: 'help', display: 'inline-flex' }}>
                            <Info size={12} />
                        </span>
                    </Tooltip>
                </span>
            ),
            selector: (row) => row.contractedValue || 0,
            sortable: true,
            cell: (row) => {
                if (row.contractedValue === null) {
                    if (row.contractedValueMethod === 'insufficient') {
                        return <span style={{ color: 'var(--text-muted)' }} title="Only 1 invoice — insufficient data to estimate">—*</span>;
                    }
                    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                }
                const methodLabel = row.contractedValueMethod === '12mo_sum'
                    ? `Sum of last 12 months invoices (${row.invoiceCount} total invoices, ${row.tenureMonths}mo tenure)`
                    : `Avg of ${(row.invoiceCount || 0) - 1} invoices (excl. first) × 12 (${row.invoiceCount} total invoices, ${row.tenureMonths}mo tenure)`;
                return (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }} title={methodLabel}>
                        {row.contractedValueMethod === 'avg_x12' && <span style={{ color: 'var(--warning-color, #f59e0b)', marginRight: 4 }}>~</span>}
                        {formatCurrency(row.contractedValue)}
                    </span>
                );
            },
            right: true,
            width: "130px"
        },
        {
            name: "Services",
            selector: (row) => row.electedServices?.length || 0,
            sortable: true,
            cell: (row) => {
                const services = row.electedServices || [];
                if (services.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                return (
                    <span style={{ color: 'var(--accent-blue)', fontSize: '0.85rem' }}>
                        {services.length} service{services.length !== 1 ? 's' : ''}
                    </span>
                );
            },
            width: "110px",
        },
        {
            name: "Year End",
            selector: (row) => row.YearEndDate,
            sortable: true,
            cell: (row) => (
                <span>{row.YearEndDate ? new Date(row.YearEndDate).toLocaleDateString('en-IE') : '—'}</span>
            ),
            width: "100px"
        },
    ];

    const downloadCSV = () => {
        const headers = [
            'Client Ref', 'Client Name', 'Status', 'Type', 'Manager Email',
            'Manager', 'Nature of Business', 'Address', 'Date of Incorporation',
            'Year End Date', 'Services', 'Fees YTD', 'Fees Outstanding',
            'Health Score', 'Health Category', 'Elected Services', 'Contracted Value', 'Calculation Method'
        ];

        const csvData = filteredClients.map(c => [
            c.ClientRef, c.Title, c.ClientState, c.ClientType, c.UserEmail,
            c.managerName || '', c.NatureOfBusiness, c.Address1,
            c.DateOfIncorporation, c.YearEndDate, c.Services,
            c.invoicedFeesYTD ?? '', c.feesOutstanding ?? '',
            c.healthScore ?? '', c.healthCategory ?? '',
            (c.electedServices || []).join('; '), c.contractedValue ?? '',
            c.contractedValueMethod ?? ''
        ]);

        const escapeCSV = (value: any): string => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...csvData.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `bss-clients-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = () => {
        const rows = filteredClients.map(c => ({
            'Client Ref': c.ClientRef,
            'Client Name': c.Title,
            'Status': c.ClientState,
            'Type': c.ClientType,
            'Manager Email': c.UserEmail,
            'Manager': c.managerName || '',
            'Nature of Business': c.NatureOfBusiness,
            'Address': c.Address1,
            'Date of Incorporation': c.DateOfIncorporation,
            'Year End Date': c.YearEndDate,
            'Services': c.Services,
            'Fees YTD': c.invoicedFeesYTD ?? '',
            'Fees Outstanding': c.feesOutstanding ?? '',
            'Health Score': c.healthScore ?? '',
            'Health Category': c.healthCategory ?? '',
            'Elected Services': (c.electedServices || []).join('; '),
            'Contracted Value': c.contractedValue ?? '',
            'Calculation Method': c.contractedValueMethod ?? '',
            'Gold Partner': c.isGoldPartner ? 'Yes' : '',
            'Partner': c.isPartner ? 'Yes' : '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clients');
        XLSX.writeFile(wb, `bss-clients-${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <FileText size={28} />
                        <h1>Client Listing</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading clients: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <FileText size={20} />
                    <h1>Client Listing</h1>
                </div>
            </div>

            <div className="stats-grid stats-grid-5">
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#f59e0b' }}>
                        <Star size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{goldPartnerCount}</div>
                        <div className="stat-label">Gold Partners</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#3b82f6' }}>
                        <Award size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{partnerCount}</div>
                        <div className="stat-label">Partners</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{clientOnlyCount}</div>
                        <div className="stat-label">Clients</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalContractedValue)}</div>
                        <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            Total Contracted
                            <Tooltip
                                title={
                                    <div style={{ whiteSpace: 'pre-line', fontSize: 12, lineHeight: 1.5 }}>
                                        Contracted value is estimated from invoices.{"\n\n"}
                                        • Clients with 12+ months tenure: sum of last 12 months invoices.{"\n"}
                                        • Clients with {"<"} 12 months tenure: average of all invoices (excluding the first) × 12.{"\n\n"}
                                        A ~ prefix indicates the avg × 12 method.
                                    </div>
                                }
                                arrow
                                placement="top"
                            >
                                <span style={{ cursor: 'help', display: 'inline-flex' }}>
                                    <Info size={14} />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: '#ef4444' }}>
                        <AlertCircle size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalOutstanding)}</div>
                        <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            Total Outstanding
                            <Tooltip
                                title={
                                    <div style={{ whiteSpace: 'pre-line', fontSize: 12, lineHeight: 1.5 }}>
                                        Sum of unpaid invoice balances (Fees Outstanding) across the clients in the current view.
                                    </div>
                                }
                                arrow
                                placement="top"
                            >
                                <span style={{ cursor: 'help', display: 'inline-flex' }}>
                                    <Info size={14} />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                        style={{ accentColor: 'var(--accent-blue)' }}
                    />
                    Active Only
                </label>
                <select
                    value={partnerFilter}
                    onChange={(e) => setPartnerFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Types</option>
                    <option value="gold">Gold Partners ({goldPartnerCount})</option>
                    <option value="partner">Partners ({partnerCount})</option>
                    <option value="client">Clients Only</option>
                </select>
                <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="filter-select"
                    disabled={activeOnly}
                    style={activeOnly ? { opacity: 0.5 } : {}}
                >
                    <option value="all">All States ({clients.length})</option>
                    {clientStates.map(s => (
                        <option key={s.state} value={s.state}>
                            {s.state} ({s.count})
                        </option>
                    ))}
                </select>
                <button
                    onClick={downloadCSV}
                    className="download-btn"
                    title="Download as CSV"
                >
                    <Download size={16} />
                    Export CSV
                </button>
                <button
                    onClick={downloadXLSX}
                    className="download-btn"
                    title="Download as Excel"
                >
                    <FileSpreadsheet size={16} />
                    Export Excel
                </button>
            </div>

            <div className="data-table-container master-services-table">
                <DataTable
                    columns={columns}
                    data={filteredClients}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    expandableRows
                    expandableRowsComponent={({ data }) => {
                        const services = data.electedServices || [];
                        return (
                            <div style={{
                                padding: '12px 24px 16px 52px',
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottom: '1px solid var(--border-color)',
                            }}>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, color: 'var(--text-primary)' }}>
                                    Elected Services for {data.Title}
                                </div>
                                {services.length === 0 ? (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No services recorded</span>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {services.map((s: string, i: number) => (
                                            <span key={i} style={{
                                                display: 'inline-block',
                                                padding: '3px 10px',
                                                borderRadius: 12,
                                                fontSize: '0.8rem',
                                                backgroundColor: 'var(--bg-hover)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-secondary)',
                                            }}>
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                    noDataComponent={
                        <div className="no-data">No clients found</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                                minHeight: '48px',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        expanderRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                color: 'var(--text-primary)',
                            },
                        },
                        expanderButton: {
                            style: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:focus': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                svg: {
                                    fill: 'var(--text-secondary)',
                                },
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                            },
                        },
                    }}
                />
            </div>

            <div
                style={{
                    marginTop: 24,
                    padding: '16px 20px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 600, color: 'var(--text-primary)' }}>
                    <Info size={16} />
                    <span>Data Sources</span>
                    <span
                        style={{
                            marginLeft: 8,
                            padding: '2px 8px',
                            background: 'var(--accent-red, #dc2626)',
                            color: '#fff',
                            borderRadius: 4,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                        }}
                    >
                        PRODUCTION DATA
                    </span>
                </div>
                <p style={{ margin: '0 0 8px 0' }}>
                    Client records come from the Kinore <code>senta_companies</code> mirror in Postgres (synced from the Senta practice-management system). Financial figures (invoiced fees, outstanding, contracted value), partner status, elected services, manager assignment and health scores come from the Kinore analytics database, which is synced from Senta and Xero.
                </p>
                <details>
                    <summary style={{ cursor: 'pointer', color: 'var(--accent-blue)', userSelect: 'none' }}>
                        Show technical details
                    </summary>
                    <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--bg-primary)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Live API</p>
                        <ul style={{ margin: '0 0 12px 18px', padding: 0 }}>
                            <li><code>GET /api/clients</code> &mdash; PostgreSQL query against the <code>senta_companies</code> mirror table (<code>api_response_json_1</code> JSONB). Returns the full client list (name, ref, state, type, address, year-end, services, key dates). <strong>Source: Kinore analytics warehouse, synced from Senta.</strong></li>
                        </ul>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Enrichment API</p>
                        <ul style={{ margin: '0 0 12px 18px', padding: 0 }}>
                            <li><code>GET /api/bss-enrichment</code> &mdash; PostgreSQL query against the Kinore analytics warehouse (Heroku Postgres, <code>eu-west-1</code>). <strong>Production database.</strong></li>
                        </ul>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Tables read</p>
                        <ul style={{ margin: '0 0 12px 18px', padding: 0 }}>
                            <li><code>xero_invoices</code> &times; <code>xero_contacts</code> &times; <code>senta_companies</code> &mdash; invoiced fees YTD (PAID + AUTHORISED, current calendar year), fees outstanding (<code>amount_due &gt; 0</code>, excluding DELETED/VOIDED), and contracted-value calculation.</li>
                            <li><code>client_health_scores</code> &times; <code>businesses</code> &mdash; latest overall health score and category (Healthy/Moderate/At Risk/Critical).</li>
                            <li><code>senta_companies.api_response_json_1.services</code> &times; <code>senta_services</code> &mdash; elected service titles.</li>
                            <li><code>staff_assignments</code> &times; <code>businesses</code> &times; <code>admin_users</code> &mdash; assigned manager name. When no staff assignment exists, the manager is <em>inferred</em> from <code>senta_companies.api_response_json_1.form._04e5d5385c99</code> (Senta user id) by mapping to <code>admin_users</code> via the most-common pairing observed across other clients. Inferred values are flagged in the Manager column.</li>
                            <li><code>senta_companies.api_response_json_2.form</code> &mdash; BSS-client flag (<code>_826df83ba74e</code>), Gold Partner flag (<code>_ec6041ccaf31</code>), Partner flag (<code>_f97ede8d8136</code>).</li>
                        </ul>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Contracted value</p>
                        <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                            <li>Tenure &ge; 12 months: sum of PAID/AUTHORISED invoice totals over the trailing 12 months.</li>
                            <li>Tenure &lt; 12 months: drop the first (oldest) invoice, average the rest, multiply by 12 (shown with a <code>~</code> prefix).</li>
                        </ul>
                    </div>
                </details>
            </div>
        </div>
    );
};

export default BssReporting;
